<script setup>
import { ref, computed, defineEmits, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TasksAPI from 'dashboard/api/tasks';
import ContactsAPI from 'dashboard/api/contacts';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { TASK_ASSIGN_ALL_USER_IDS } from 'dashboard/constants/taskPermissions';

const props = defineProps({
  prefillConversationId: { type: [Number, String], default: null },
  prefillContactName: { type: String, default: '' },
  prefillContactId: { type: [Number, String], default: null },
  editingTask: { type: Object, default: null }, // Fase 3 · modo edición
});
const emit = defineEmits(['close', 'created', 'updated']);

const isEditMode = computed(() => !!props.editingTask);

const store = useStore();
const title = ref('');
const description = ref('');
const assigneeId = ref(null);
const dueDate = ref('');
const priority = ref('normal');
const saving = ref(false);

// Vincular cliente (Fase 2)
const linkedContact = ref(null); // { id, name, phone? }
const contactSearch = ref('');
const contactResults = ref([]);
const showResults = ref(false);
const searching = ref(false);
let searchTimer = null;

onMounted(() => {
  if (props.editingTask) {
    // Modo edición: pre-llenar con los valores actuales de la tarea
    const t = props.editingTask;
    title.value = t.title || '';
    description.value = t.description || '';
    assigneeId.value = t.assignee_id;
    priority.value = t.priority || 'normal';
    if (t.due_date) {
      const d = new Date(t.due_date * 1000);
      // Format para input datetime-local: YYYY-MM-DDTHH:MM
      const pad = n => String(n).padStart(2, '0');
      dueDate.value = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    if (t.contact) {
      linkedContact.value = { id: t.contact.id, name: t.contact.name, phone: t.contact.phone };
    }
  } else if (props.prefillContactName) {
    linkedContact.value = {
      id: props.prefillContactId,
      name: props.prefillContactName,
      _fromConversation: !!props.prefillConversationId,
    };
  }
});

function onSearchInput() {
  if (searchTimer) clearTimeout(searchTimer);
  const q = contactSearch.value.trim();
  if (q.length < 2) {
    contactResults.value = [];
    showResults.value = false;
    return;
  }
  searchTimer = setTimeout(async () => {
    searching.value = true;
    try {
      const { data } = await ContactsAPI.search(q, 1, 'name', '');
      contactResults.value = (data?.payload || []).slice(0, 8);
      showResults.value = true;
    } catch (_) {
      contactResults.value = [];
    } finally {
      searching.value = false;
    }
  }, 300);
}

function pickContact(c) {
  linkedContact.value = { id: c.id, name: c.name, phone: c.phone_number };
  contactSearch.value = '';
  showResults.value = false;
}

function clearLinkedContact() {
  linkedContact.value = null;
  contactSearch.value = '';
}

// Asignar tarea solo a personal de las sucursales a las que el que asigna
// Agentes en el desplegable de asignar (Victor 2026-06-03):
//   - Super admin + grupo "ven todos" (Victor Guzmán 3 / Victor Manuel 7 /
//     Gabriela 41) → TODOS los agentes.
//   - Encargadas de sucursal (Rubi 19 / Karina 6 / Kemely 4) → SOLO agentes
//     cuya SUCURSAL PRINCIPAL coincide con la de ellas.
const { isSuperAdmin } = usePermissions();
const agents = computed(() => {
  const all = store.getters['agents/getAgents'] || [];
  const me = store.getters.getCurrentUser || {};
  if (isSuperAdmin.value || TASK_ASSIGN_ALL_USER_IDS.includes(me.id)) return all;
  const myBranch = me.primary_branch_id ?? null;
  if (myBranch == null) return all;
  return all.filter(a => a.primary_branch_id === myBranch);
});

async function submit() {
  if (!title.value.trim()) {
    useAlert('La tarea necesita un título.');
    return;
  }
  if (!assigneeId.value) {
    useAlert('Selecciona un agente para asignar.');
    return;
  }
  saving.value = true;
  try {
    const payload = {
      title: title.value.trim(),
      description: description.value.trim(),
      assignee_id: assigneeId.value,
      priority: priority.value,
    };
    if (dueDate.value) {
      payload.due_date = new Date(dueDate.value).toISOString();
    }
    if (props.prefillConversationId) {
      payload.conversation_id = props.prefillConversationId;
    }
    if (linkedContact.value && !payload.conversation_id) {
      payload.contact_id = linkedContact.value.id;
    }
    if (isEditMode.value) {
      await TasksAPI.update(props.editingTask.id, payload);
      useAlert('✓ Tarea actualizada.');
      emit('updated');
    } else {
      await TasksAPI.create(payload);
      useAlert('✓ Tarea creada.');
      emit('created');
    }
    emit('close');
  } catch (err) {
    useAlert(err.response?.data?.error || (isEditMode.value ? 'No se pudo actualizar.' : 'No se pudo crear la tarea.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="ntm-backdrop" @click.self="emit('close')">
      <div class="ntm-modal">
        <div class="ntm-header">
          <h2>{{ isEditMode ? '✏️ Editar tarea' : '📋 Nueva tarea' }}</h2>
          <button class="ntm-close" @click="emit('close')">✕</button>
        </div>

      <div class="ntm-body">
        <div v-if="prefillContactName && prefillConversationId" class="ntm-client-banner">
          🔗 Tarea vinculada al cliente <b>{{ prefillContactName }}</b>
        </div>

        <!-- Vincular a cliente (opcional · solo si no viene prefilled de un chat) -->
        <div v-if="!prefillConversationId" class="ntm-field">
          <label>Vincular a cliente (opcional)</label>
          <div v-if="linkedContact" class="ntm-linked-contact">
            <span class="ntm-linked-icon">🔗</span>
            <div class="ntm-linked-info">
              <div class="ntm-linked-name">{{ linkedContact.name }}</div>
              <div v-if="linkedContact.phone" class="ntm-linked-phone">{{ linkedContact.phone }}</div>
            </div>
            <button type="button" class="ntm-linked-clear" @click="clearLinkedContact">✕ Quitar</button>
          </div>
          <div v-else class="ntm-search-wrap">
            <input
              v-model="contactSearch"
              type="text"
              placeholder="🔍 Busca por nombre o teléfono (mínimo 2 caracteres)"
              @input="onSearchInput"
              @focus="onSearchInput"
              @blur="setTimeout(() => showResults = false, 200)"
            />
            <div v-if="showResults && contactResults.length" class="ntm-search-results">
              <div
                v-for="c in contactResults"
                :key="c.id"
                class="ntm-search-item"
                @mousedown.prevent="pickContact(c)"
              >
                <div class="ntm-search-name">{{ c.name }}</div>
                <div v-if="c.phone_number" class="ntm-search-phone">{{ c.phone_number }}</div>
              </div>
            </div>
            <div v-if="searching" class="ntm-search-hint">Buscando…</div>
          </div>
        </div>

        <div class="ntm-field">
          <label>Título *</label>
          <input v-model="title" type="text" placeholder="Ej: Llamar a Norma Netro" maxlength="200" />
        </div>

        <div class="ntm-field">
          <label>Descripción</label>
          <textarea v-model="description" rows="4" placeholder="Detalles adicionales (opcional)" maxlength="5000"></textarea>
        </div>

        <div class="ntm-row">
          <div class="ntm-field">
            <label>Asignar a *</label>
            <select v-model="assigneeId">
              <option :value="null">— Selecciona agente —</option>
              <option v-for="a in agents" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
          </div>
          <div class="ntm-field">
            <label>Vence</label>
            <input v-model="dueDate" type="datetime-local" />
          </div>
          <div class="ntm-field">
            <label>Prioridad</label>
            <select v-model="priority">
              <option value="normal">— Normal (aviso al iniciar sesión)</option>
              <option value="alta">▲ Alta (recordatorio c/1 h)</option>
              <option value="urgente">⚠ Urgente (recordatorio c/5 min)</option>
            </select>
          </div>
        </div>
      </div>

        <div class="ntm-footer">
          <button class="ntm-btn-cancel" @click="emit('close')">Cancelar</button>
          <button class="ntm-btn-save" :disabled="saving" @click="submit">{{ saving ? (isEditMode ? 'Guardando…' : 'Creando…') : (isEditMode ? 'Guardar cambios' : 'Crear tarea') }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ntm-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.65); display: flex; align-items: center; justify-content: center; z-index: 9000; backdrop-filter: blur(6px); }
.ntm-modal { width: 560px; max-width: calc(100vw - 32px); background: linear-gradient(180deg, #0d0d0d, #060606); border: 1px solid rgba(251,191,36,0.25); border-radius: 12px; box-shadow: 0 20px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(251,191,36,0.10); overflow: hidden; }
.ntm-header { display: flex; align-items: center; padding: 16px 20px; border-bottom: 1px solid rgba(255,255,255,0.06); }
.ntm-header h2 { font-size: 15px; font-weight: 800; color: #fff; flex: 1; margin: 0; }
.ntm-close { background: transparent; border: 0; color: rgba(255,255,255,0.5); font-size: 16px; cursor: pointer; padding: 4px 8px; }
.ntm-close:hover { color: #fff; }

.ntm-body { padding: 18px 20px; max-height: 60vh; overflow-y: auto; }
.ntm-client-banner { padding: 10px 12px; background: rgba(168,85,247,0.10); border: 1px solid rgba(168,85,247,0.30); border-radius: 7px; font-size: 12px; color: #c4b5fd; margin-bottom: 14px; }
.ntm-client-banner b { color: #ddd6fe; }

.ntm-field { margin-bottom: 14px; }
.ntm-field label { display: block; font-size: 10px; font-weight: 900; letter-spacing: 1.3px; color: rgba(255,255,255,0.5); text-transform: uppercase; margin-bottom: 5px; }
.ntm-field input, .ntm-field textarea, .ntm-field select { width: 100%; padding: 8px 11px; background: rgba(0,0,0,0.30); border: 1px solid rgba(255,255,255,0.10); border-radius: 7px; color: #fff; font-size: 13px; font-family: inherit; }
.ntm-field input:focus, .ntm-field textarea:focus, .ntm-field select:focus { outline: none; border-color: rgba(251,191,36,0.45); }
.ntm-field textarea { resize: vertical; min-height: 80px; }

.ntm-row { display: grid; grid-template-columns: 1.4fr 1fr 0.9fr; gap: 10px; }

.ntm-footer { display: flex; gap: 8px; justify-content: flex-end; padding: 14px 20px; border-top: 1px solid rgba(255,255,255,0.06); background: rgba(0,0,0,0.30); }
.ntm-btn-cancel { padding: 8px 14px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 7px; color: rgba(255,255,255,0.7); font-size: 12px; cursor: pointer; }
.ntm-btn-save { padding: 8px 16px; background: linear-gradient(135deg, #fbbf24, #d97706); border: 0; border-radius: 7px; color: #1a1209; font-size: 12px; font-weight: 800; cursor: pointer; box-shadow: 0 3px 10px rgba(251,191,36,0.30); }
.ntm-btn-save:disabled { opacity: 0.5; cursor: not-allowed; }

/* Linked contact (Fase 2) */
.ntm-linked-contact { display: flex; align-items: center; gap: 10px; padding: 8px 12px; background: rgba(168,85,247,0.08); border: 1px solid rgba(168,85,247,0.30); border-radius: 8px; }
.ntm-linked-icon { font-size: 18px; }
.ntm-linked-info { flex: 1; }
.ntm-linked-name { font-size: 13px; font-weight: 700; color: #ddd6fe; }
.ntm-linked-phone { font-size: 11px; color: rgba(196,181,253,0.75); }
.ntm-linked-clear { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 5px; color: rgba(255,255,255,0.6); font-size: 10px; padding: 3px 8px; cursor: pointer; }
.ntm-linked-clear:hover { background: rgba(239,68,68,0.10); color: #fca5a5; border-color: rgba(239,68,68,0.30); }

.ntm-search-wrap { position: relative; }
.ntm-search-results { position: absolute; top: 100%; left: 0; right: 0; margin-top: 4px; background: #0b0b0b; border: 1px solid rgba(255,255,255,0.12); border-radius: 7px; max-height: 220px; overflow-y: auto; box-shadow: 0 8px 24px rgba(0,0,0,0.5); z-index: 10; }
.ntm-search-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.04); }
.ntm-search-item:hover { background: rgba(251,191,36,0.08); }
.ntm-search-item:last-child { border-bottom: 0; }
.ntm-search-name { font-size: 13px; font-weight: 600; color: #fff; }
.ntm-search-phone { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 1px; }
.ntm-search-hint { padding: 6px 10px; font-size: 11px; color: rgba(255,255,255,0.45); font-style: italic; }
</style>
